import React, { useEffect, useState, useRef } from "react";
import api from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Loader2 } from "lucide-react";

export default function ChangeLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollAreaRef = useRef(null);
  const [searchPlate, setSearchPlate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { isAuthenticated } = useAuth();
  const formatStatus = (text) => {
  return text
    .replaceAll("_", " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    api.get("/api/vehicle-history/")
      .then(res => {
        const sorted = res.data
          .filter(log => {
  // Always include create/delete logs
  if (log.history_type === "+" || log.history_type === "-") return true;

  // Skip if changes array is exactly ["No relevant field changes."]
  if (
    Array.isArray(log.changes) &&
    log.changes.length === 1 &&
    log.changes[0] === "No relevant field changes."
  ) {
    return false;
  }

  // Keep logs that have actual changes
  return Array.isArray(log.changes) && log.changes.length > 0;
})
          .sort((a, b) => new Date(b.history_date) - new Date(a.history_date));
        setLogs(sorted);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch logs", err);
        setLoading(false);
      });
   if (scrollAreaRef.current) {
    scrollAreaRef.current.scrollTop = 0;
  }
  }, [page, isAuthenticated]);

  // Apply filters
  const filteredLogs = logs.filter(log => {
  const matchesPlate = log.plate_number
    ?.toLowerCase()
    .includes(searchPlate.toLowerCase());

  const logDate = new Date(log.history_date);

  const logDay = new Date(
    logDate.getFullYear(),
    logDate.getMonth(),
    logDate.getDate()
  );

  const startDay = startDate
    ? new Date(new Date(startDate).setHours(0, 0, 0, 0))
    : null;
  const endDay = endDate
    ? new Date(new Date(endDate).setHours(23, 59, 59, 999))
    : null;

  const matchesStart = startDay ? logDay >= startDay : true;
  const matchesEnd = endDay ? logDay <= endDay : true;

  return matchesPlate && matchesStart && matchesEnd;
});

  const totalPages = Math.ceil(filteredLogs.length / pageSize);
  const start = (page - 1) * pageSize;
  const currentLogs = filteredLogs.slice(start, start + pageSize);

  const renderLogMessage = (log) => {
    if (log.history_type === "+") return <li>🚗 A new vehicle was added.</li>;
    if (log.history_type === "-") return <li>🗑️ Vehicle was removed from the list.</li>;
    if (
    Array.isArray(log.changes) &&
    log.changes.length === 1 &&
    log.changes[0] === "No relevant field changes."
  ) {
    return <li className="italic text-muted-foreground">No relevant field changes.</li>;
  }

  return log.changes.map((change, i) => {
  let formatted = change;

  // Apply formatting to status changes
  if (change.startsWith("Status changed from '") && change.includes("' to '")) {
    formatted = change.replace(
      /Status changed from '(.+?)' to '(.+?)'/,
      (_, from, to) =>
        `Status changed from ${formatStatus(from)} to ${formatStatus(to)}`
    );
  }

  return <li key={i}>✏️ {formatted}</li>;
});
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setPage(1); // Reset to first page
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Vehicle Change Log</h2>

      {/* Filters with labels */}
      <div className="flex flex-wrap gap-4 items-end mb-4">
  <div>
    <label className="block mb-1">Plate Number</label>
    <input
      type="text"
      value={searchPlate}
      onChange={(e) => {
        setSearchPlate(e.target.value);
        setPage(1);
      }}
      placeholder="Enter plate number"
      className="border rounded px-2 py-1 text-sm"
    />
  </div>

  <div>
    <label className="block mb-1">Start Date</label>
    <input
      type="date"
      value={startDate}
      onChange={(e) => {
        setStartDate(e.target.value);
        setPage(1);
      }}
      className="border rounded px-2 py-1 text-sm"
    />
  </div>

  <div>
    <label className="block mb-1">End Date</label>
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={endDate}
        onChange={(e) => {
          setEndDate(e.target.value);
          setPage(1);
        }}
        className="border rounded px-2 py-1 text-sm"
      />
      <button
        onClick={() => {
          setSearchPlate("");
          setStartDate("");
          setEndDate("");
          setPage(1);
        }}
        className="px-3 py-1 border rounded bg-gray-100 hover:bg-gray-200 text-sm"
      >
        Reset Filters
      </button>
    </div>
  </div>

  <div className="ml-auto">
    <label className="block mb-1">Logs per Page</label>
    <select
      value={pageSize}
      onChange={handlePageSizeChange}
      className="border rounded px-2 py-1 text-sm"
    >
      {[50, 100, 500].map(size => (
        <option key={size} value={size}>{size} per page</option>
      ))}
    </select>
  </div>
</div>
      {/* Main Display */}
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="animate-spin" />
          Loading logs...
        </div>
      ) : (
        <>
          <ScrollArea ref={scrollAreaRef} className="max-h-[680px] overflow-y-auto rounded border p-2">
            {currentLogs.length === 0 ? (
              <div className="text-muted-foreground italic">No logs found.</div>
            ) : (
              Object.entries(
            currentLogs.reduce((grouped, log) => {
              const date = new Date(log.history_date).toLocaleDateString("en-GB", {
                year: "numeric",
                month: "long",
                day: "numeric",
              });
              if (!grouped[date]) grouped[date] = [];
              grouped[date].push(log);
              return grouped;
            }, {})
          ).map(([date, logsOnDate], idx) => (
            <div key={idx} className="mb-4">
              <h3 className="font-semibold text-lg mb-2">{date}</h3>
              {logsOnDate.map((log, i) => {
                const time = new Date(log.history_date).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                });
                const user = log.history_user || "System";
                const plate = log.plate_number;

                let message = "";
                let emoji = "✏️";

                if (log.history_type === "+") {
                  emoji = "🚗";
                  message = `(A new vehicle ${plate} was added)`;
                } else if (log.history_type === "-") {
                  emoji = "🗑️";
                  message = `(Vehicle ${plate} was removed from the list)`;
                } else if (
                  Array.isArray(log.changes) &&
                  log.changes.length === 1 &&
                  log.changes[0] === "No relevant field changes."
                ) {
                  return null; // Skip
                } else {
                  const formattedChanges = log.changes.map((change) => {
                    if (change.startsWith("Status changed from '")) {
                      return change.replace(
                        /Status changed from '(.+?)' to '(.+?)'/,
                        (_, from, to) =>
                          `Status changed from ${formatStatus(from)} to ${formatStatus(to)}`
                      );
                    }
                    return change;
                  });
                  message = `(${formattedChanges.join(", ")})`;
                }

return (
  <div key={i} className="text-sm mb-2">
    <div className="pl-2 flex gap-2">
      <span className="font-mono">{time}</span>
      <span>
        {plate}, {user} →
      </span>
    </div>
    {log.history_type === "+" || log.history_type === "-" ? (
      <div className="pl-10 flex gap-2">
        <span className="w-6">{emoji}</span>
        <span>{message}</span>
      </div>
    ) : (
      log.changes.map((change, idx) => {
        let formatted = change;
        if (change.startsWith("Status changed from '")) {
  formatted = change.replace(
    /Status changed from '(.+?)' to '(.+?)'/,
    (_, from, to) =>
      `Status changed from ${formatStatus(from)} to ${formatStatus(to)}`
  );
} else if (change.startsWith("Company Name changed from '")) {
  formatted = change.replace(
    /Company Name changed from '(.+?)' to '(.+?)'/,
    (_, from, to) =>
      `Company Name changed from ${formatStatus(from)} to ${formatStatus(to)}`
  );
}
        return (
          <div key={idx} className="pl-10 flex gap-2">
            <span className="w-6">✏️</span>
            <span>{formatted}</span>
          </div>
        );
      })
    )}
  </div>
);
              })}
            </div>
          ))
            )}
          </ScrollArea>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <div className="space-x-2">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
